const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC"
});

const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2
});

const decimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 4
});
const ratioFormatter = new Intl.NumberFormat("en-US", {
  maximumSignificantDigits: 12
});

export class FundamentalEventTooltip {
  constructor(stage, trigger, id) {
    if (!(stage instanceof HTMLElement)) {
      throw new TypeError("A fundamental-event tooltip requires a chart stage.");
    }
    if (!(trigger instanceof HTMLCanvasElement)) {
      throw new TypeError("A fundamental-event tooltip requires its chart canvas.");
    }

    this.stage = stage;
    this.trigger = trigger;
    for (const existing of stage.querySelectorAll("[data-chart-event-tooltip]")) {
      existing.remove();
    }
    this.element = document.createElement("aside");
    this.element.id = id;
    this.element.className = "chart-event-tooltip";
    this.element.dataset.chartEventTooltip = "";
    this.element.setAttribute("role", "tooltip");
    this.element.hidden = true;

    this.heading = document.createElement("div");
    this.heading.className = "chart-event-tooltip-heading";
    this.badge = document.createElement("span");
    this.badge.className = "chart-event-tooltip-badge";
    this.headingText = document.createElement("div");
    this.type = document.createElement("span");
    this.type.className = "chart-event-tooltip-type";
    this.date = document.createElement("time");
    this.date.className = "chart-event-tooltip-date";
    this.headingText.append(this.type, this.date);
    this.status = document.createElement("span");
    this.status.className = "chart-event-tooltip-status";
    this.heading.append(this.badge, this.headingText, this.status);

    this.title = document.createElement("strong");
    this.title.className = "chart-event-tooltip-title";
    this.details = document.createElement("dl");
    this.details.className = "chart-event-tooltip-details";
    this.footer = document.createElement("footer");
    this.verification = document.createElement("span");
    this.confidence = document.createElement("span");
    this.footer.append(this.verification, this.confidence);
    this.element.append(this.heading, this.title, this.details, this.footer);
    stage.append(this.element);
  }

  show(marker) {
    if (!marker?.event) {
      this.hide();
      return;
    }

    const event = marker.event;
    const type = normalizeEventType(event.type);
    this.element.dataset.eventType = type.toLowerCase();
    this.badge.textContent = eventTypeLabel(type);
    this.type.textContent = type;
    this.date.dateTime = String(event.date || "");
    this.date.textContent = `${humanize(event.dateType || "Event date")} · ${formatEventDate(event.date)}`;
    this.status.textContent = humanize(event.status || "Unverified");
    this.title.textContent = event.title || `${type} event`;
    this.verification.textContent = fundamentalEventVerificationLabel(event);
    this.verification.dataset.verification = event.isVerified === true
      ? "verified"
      : "unverified";
    this.confidence.textContent = `Confidence ${formatConfidence(event.confidence)}`;

    this.details.replaceChildren(
      ...fundamentalEventDetailRows(event).flatMap(({ label, value }) => {
        const term = document.createElement("dt");
        const detail = document.createElement("dd");
        term.textContent = label;
        detail.textContent = value;
        if (["Validation", "EPS basis", "Data attribution"].includes(label)) detail.className = "chart-event-tooltip-disclosure";
        return [term, detail];
      })
    );

    this.element.hidden = false;
    this.trigger.classList.add("is-fundamental-event-hovered");
    addDescribedBy(this.trigger, this.element.id);
    this.position(marker.x, marker.y);
  }

  hide() {
    if (this.element.hidden) return;
    this.element.hidden = true;
    this.trigger.classList.remove("is-fundamental-event-hovered");
    removeDescribedBy(this.trigger, this.element.id);
  }

  position(markerX, markerY) {
    const inset = 8;
    const gap = 16;
    const width = this.element.offsetWidth;
    const height = this.element.offsetHeight;
    const stageWidth = this.stage.clientWidth;
    const stageHeight = this.stage.clientHeight;
    const preferredLeft = markerX + gap;
    const left = preferredLeft + width <= stageWidth - inset
      ? preferredLeft
      : markerX - width - gap;
    const top = markerY - height / 2;
    this.element.style.left = `${clamp(left, inset, Math.max(inset, stageWidth - width - inset))}px`;
    this.element.style.top = `${clamp(top, inset, Math.max(inset, stageHeight - height - inset))}px`;
  }

  dispose() {
    this.hide();
    this.element.remove();
  }
}

export function fundamentalEventDetailRows(event) {
  const rows = [];
  if (event?.earnings) {
    const earnings = event.earnings;
    const fiscalPeriod = [
      earnings.fiscalQuarter ? `Q${earnings.fiscalQuarter}` : null,
      earnings.fiscalYear
    ].filter(Boolean).join(" ");
    addRow(rows, "Fiscal period", fiscalPeriod);
    addRow(rows, "Provider period end", formatOptionalDate(earnings.providerFiscalQuarterEnd));
    addRow(
      rows,
      "Report time",
      earnings.reportTime && earnings.reportTime !== "Unspecified"
        ? humanize(earnings.reportTime)
        : null
    );
    addRow(rows, "Actual EPS", formatDecimal(earnings.epsActual));
    addRow(rows, "Estimated EPS", formatDecimal(earnings.epsEstimated));
    addRow(
      rows,
      "Actual revenue",
      formatFinancialValue(earnings.revenueActual)
    );
    addRow(
      rows,
      "Estimated revenue",
      formatFinancialValue(earnings.revenueEstimated)
    );
    addRow(rows, "Source", earnings.sourceName);
    addRow(rows, "Data attribution", earnings.attributedDataSource);
    addRow(rows, "EPS basis", earnings.epsBasisNote);
    addRow(rows, "Validation", earnings.verificationNote);
    for (const report of earnings.additionalReports || []) {
      addRow(rows, "Additional report", report.sourceName);
      addRow(rows, "Provider actual EPS", formatDecimal(report.epsActual));
      addRow(rows, "Provider estimated EPS", formatDecimal(report.epsEstimated));
      addRow(rows, "Provider period end", formatOptionalDate(report.providerFiscalQuarterEnd));
      addRow(rows, "EPS basis", report.epsBasisNote);
      addRow(rows, "Data attribution", report.attributedDataSource);
      addRow(rows, "Validation", report.verificationNote);
    }
  } else if (event?.dividend) {
    const dividend = event.dividend;
    addRow(
      rows,
      "Amount",
      formatFinancialValue(dividend.amount, dividend.currency, false)
    );
    addRow(rows, "Dividend type", humanize(dividend.dividendType));
    addRow(rows, "Ex-dividend date", formatOptionalDate(dividend.exDividendDate));
    addRow(rows, "Payment date", formatOptionalDate(dividend.paymentDate));
  } else if (event?.split) {
    const split = event.split;
    const ratio = validNumber(split.numerator) && validNumber(split.denominator)
      ? `${ratioFormatter.format(split.numerator)}-for-${ratioFormatter.format(split.denominator)}`
      : null;
    addRow(rows, "Ratio", ratio);
    addRow(
      rows,
      "Split factor",
      validNumber(split.splitFactor)
        ? ratioFormatter.format(split.splitFactor)
        : null
    );
    addRow(rows, "Split type", humanize(split.splitType));
    addRow(rows, "Execution date", formatOptionalDate(split.executionDate));
    addRow(rows, "Source", split.sourceName);
    addRow(rows, "Validation", split.verificationNote);
  }

  if (rows.length === 0) {
    rows.push({ label: "Details", value: "Announcement only" });
  }
  return rows;
}

export function fundamentalEventVerificationLabel(event) {
  if (event?.earnings?.verificationNote?.includes("financial values remain under review")) {
    return "Financial values under review";
  }
  if (event?.earnings?.sourceName === "AlphaQuery") {
    return event.isVerified === true ? "Provider-reported · validated" : "Provider-reported · unverified";
  }
  return event?.split?.sourceName === "TipRanks"
    ? event.isVerified === true ? "Provider-reported · validated" : "Provider-reported · unverified"
    : event?.isVerified === true ? "Verified" : "Unverified";
}

export function formatEventDate(value) {
  const timestamp = Date.parse(`${value || ""}T00:00:00Z`);
  return Number.isFinite(timestamp)
    ? dateFormatter.format(new Date(timestamp))
    : "Date unavailable";
}

function formatOptionalDate(value) {
  return value ? formatEventDate(value) : null;
}

function formatConfidence(value) {
  const confidence = Number(value);
  return Number.isFinite(confidence)
    ? `${Math.round(clamp(confidence, 0, 1) * 100)}%`
    : "unavailable";
}

function formatDecimal(value) {
  return validNumber(value) ? decimalFormatter.format(Number(value)) : null;
}

function formatFinancialValue(value, currency = null, compact = true) {
  if (!validNumber(value)) return null;
  const numeric = Number(value);
  if (/^[A-Z]{3}$/.test(String(currency || ""))) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: compact ? "compact" : "standard",
      maximumFractionDigits: compact ? 2 : 4
    }).format(numeric);
  }
  return compact
    ? compactNumberFormatter.format(numeric)
    : decimalFormatter.format(numeric);
}

function addRow(rows, label, value) {
  if (value === null || value === undefined || value === "") return;
  rows.push({ label, value: String(value) });
}

function normalizeEventType(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "earnings") return "Earnings";
  if (normalized === "dividend") return "Dividend";
  if (normalized === "split") return "Split";
  return "Event";
}

function eventTypeLabel(type) {
  if (type === "Earnings") return "E";
  if (type === "Dividend") return "D";
  if (type === "Split") return "S";
  return "·";
}

function humanize(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function validNumber(value) {
  return value !== null && value !== undefined && value !== "" &&
    Number.isFinite(Number(value));
}

function addDescribedBy(element, id) {
  const ids = new Set(
    String(element.getAttribute("aria-describedby") || "")
      .split(/\s+/)
      .filter(Boolean)
  );
  ids.add(id);
  element.setAttribute("aria-describedby", [...ids].join(" "));
}

function removeDescribedBy(element, id) {
  const ids = String(element.getAttribute("aria-describedby") || "")
    .split(/\s+/)
    .filter((value) => value && value !== id);
  if (ids.length > 0) element.setAttribute("aria-describedby", ids.join(" "));
  else element.removeAttribute("aria-describedby");
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}
